import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Cakes, CakeSizeOptions, CakeFlavourOptions } from '../components/HomeComponents/TopCakes/cakeIds';
import { Radio, Input, InputNumber, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { WEDDING_CAKES, isWeddingCake } from '../components/HomeComponents/WeddingCakes/weddingCakeData';
import OrderButton from '../components/OrderButton/OrderButton';

const cakeImageById = {
  ...Object.fromEntries(WEDDING_CAKES.map(({ id, image }) => [id, image])),
  [Cakes.RAFAELLO]: '/assets/cakes/raffaello-cake.jpg',
  [Cakes.CROWN]: '/assets/cakes/crown-cake.jpg',
  [Cakes.CINNAMOROLL_CAKE]: '/assets/cakes/cinnamoroll-cake.jpg',
  [Cakes.CUSTOMIZED_BDAY_CAKE]: '/assets/cakes/custom-cake.jpg',
  [Cakes.HONEY_CAKE]: '/assets/cakes/honey-cake.jpg',
  [Cakes.NAPOLEON_PLAIN]: '/assets/cakes/napaleon-2.jpg',
  [Cakes.SAMAR_CAKE]: '/assets/cakes/kids-birthday-cake-construction.jpg',
  [Cakes.BDAY_FRUIT_CAKE]: '/assets/cakes/birthday-fruit-cake.jpg',
  [Cakes.NUMBERS_BDAY_CAKE]: '/assets/cakes/numbers-cake.jpg',
  [Cakes.NAPOLEON_WITH_BERRIES]: '/assets/cakes/napaleon-with-berries.jpg',
  [Cakes.BLACK_CAKE]: '/assets/cakes/black-cake.jpg',
  [Cakes.BABY_SHOWER_CAKE]: '/assets/cakes/baby-shower.jpg',
  [Cakes.GRADUATION_CAKE]: '/assets/cakes/graduation-cake.jpeg',
  [Cakes.GRADUATION_CAKE_2]: '/assets/cakes/graduation-cake-2.jpg',
  [Cakes.WEDDING_CAKE]: '/assets/cakes/wedding-cakes.jpg',
  [Cakes.BIRTHDAY_QUEEN]: '/assets/cakes/birthday-queen.jpg',
  [Cakes.PIKACHU_CAKE]: '/assets/cakes/pikachu-cake.jpg',
  [Cakes.KUROMI_CAKE]: '/assets/cakes/kuromi.jpg',
  [Cakes.PEPPA_PIG_CAKE]: '/assets/cakes/peppa-pig.jpg',
  [Cakes.WAFFLE_ROSE_CAKE]: '/assets/cakes/waffle-rose-cake.jpg',
};

const Cake = () => {
  const { cakeId } = useParams();
  const src = cakeImageById[cakeId] || '/assets/nora-cake-logo.png';
  const isNewWeddingCake = WEDDING_CAKES.some(({ id }) => id === cakeId);
  const sizeOptions = CakeSizeOptions[cakeId] || CakeSizeOptions.DEFAULT;
  const flavourOptions = CakeFlavourOptions[cakeId] || CakeFlavourOptions.DEFAULT;
  const [selectedSize, setSelectedSize] = useState(sizeOptions[0]?.id);
  const [selectedFlavour, setSelectedFlavour] = useState(flavourOptions[0]?.id);
  const [cakeMessage, setCakeMessage] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [customerName, setCustomerName] = useState('');
  const [customerRequirement, setCustomerRequirement] = useState('');
  const [customerPreferredDate, setCustomerPreferredDate] = useState('');
  const [deliveryValue, setDeliveryValue] = useState(1);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  
  const today = dayjs();
  const minDate = today.add(1, 'day');
  // Weddings are booked well ahead, so allow a longer window than regular cakes.
  const maxDate = isWeddingCake(cakeId) ? today.add(18, 'month') : today.add(30, 'day');
  
  const cakeNameById = {
    ...Object.fromEntries(WEDDING_CAKES.map(({ id, name }) => [id, name])),
    [Cakes.RAFAELLO]: 'Raffaello cake',
    [Cakes.CROWN]: 'Crown birthday/wedding cake',
    [Cakes.CINNAMOROLL_CAKE]: 'Cinnamoroll cake',
    [Cakes.CUSTOMIZED_BDAY_CAKE]: 'Customized birthday/wedding cake',
    [Cakes.HONEY_CAKE]: 'Honey cake',
    [Cakes.NAPOLEON_PLAIN]: 'Classic Napoleon cake',
    [Cakes.SAMAR_CAKE]: 'Truffle Kids birthday Samar cake',
    [Cakes.NUMBERS_BDAY_CAKE]: 'Numbers birthday cake',
    [Cakes.NAPOLEON_WITH_BERRIES]: 'Napoleon with berries',
    [Cakes.BLACK_CAKE]: 'Black cake',
    [Cakes.BABY_SHOWER_CAKE]: 'Baby Shower cake',
    [Cakes.GRADUATION_CAKE]: 'Graduation cake',
    [Cakes.GRADUATION_CAKE_2]: 'Graduation cake',
    [Cakes.WEDDING_CAKE]: 'Wedding cake',
    [Cakes.BIRTHDAY_QUEEN]: 'Birthday Queen cake',
    [Cakes.PIKACHU_CAKE]: 'Pikachu cake',
    [Cakes.KUROMI_CAKE]: 'Kuromi cake',
    [Cakes.PEPPA_PIG_CAKE]: 'Peppa Pig cake',
    [Cakes.WAFFLE_ROSE_CAKE]: 'Waffle Rose cake',
  };
  const displayName = cakeNameById[cakeId] || 'Cake';

  const selectedSizeLabel = sizeOptions.find((option) => option.id === selectedSize)?.label;
  const selectedFlavourLabel = flavourOptions.find((option) => option.id === selectedFlavour)?.label;

  return (
    <div style={{ 
      minHeight: '100vh', 
      padding: '24px 16px', 
      display: 'flex', 
      alignItems: 'flex-start', 
      justifyContent: 'center',
      overflowY: 'auto'
    }}>
      <div
        style={{
          display: 'flex',
          gap: 24,
          alignItems: 'flex-start',
          justifyContent: 'center',
          flexWrap: 'wrap',
          width: '100%',
          maxWidth: 900,
          margin: '0 auto',
          paddingBottom: '40px'
        }}
      >
        <div style={{ flex: '0 1 420px', maxWidth: 420, minWidth: 280 }}>
          <img
            src={src}
            alt={cakeId}
            style={{ width: '100%', height: 'auto', borderRadius: 12, display: 'block' }}
          />
        </div>
        <div style={{ flex: '1 1 320px', minHeight: 200, minWidth: 280 }}>
          <h1 style={{ fontSize: 'clamp(24px, 5vw, 44px)', margin: '0 0 16px 0' }}>{displayName}</h1>
          <div style={{ marginTop: 8 }}>
            {isNewWeddingCake ? (
              <>
                <h3 style={{ margin: '16px 0 8px 0' }}>{sizeOptions[0].label}</h3>
                <p style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 700, color: '#c2185b' }}>{WEDDING_CAKES.find(({ id }) => id === cakeId).price}</p>
              </>
            ) : (
              <>
                <h3 style={{ margin: '16px 0 8px 0' }}>Size options</h3>
                <Radio.Group
                  onChange={(e) => setSelectedSize(e.target.value)}
                  value={selectedSize}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8}}>
                    {sizeOptions.map((option) => (
                      <Radio key={option.id} value={option.id}>{option.label}</Radio>
                    ))}
                  </div>
                </Radio.Group>
              </>
            )}
            <p style={{ marginTop: 16, fontSize: 18, color: '#666' }}>
              {isNewWeddingCake
                ? 'Contact us if you would like to change tier and serve amount'
                : 'Please confirm any major customization requests with call/sms before ordering!'}
            </p>
            
            <div style={{ marginTop: 24 }}>
              <h3 style={{ margin: '16px 0 8px 0' }}>Choose a Flavour</h3>
              <Radio.Group
                onChange={(e) => setSelectedFlavour(e.target.value)}
                value={selectedFlavour}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
                  {flavourOptions.map((option) => (
                    <Radio key={option.id} value={option.id}>{option.label}</Radio>
                  ))}
                </div>
              </Radio.Group>
              
              <div style={{ marginTop: 16 }}>
                <h6 style={{ margin: '16px 0 8px 0' }}>Name/Message to write on Cake (If any)</h6>
                <Input
                  placeholder="Enter name or message..."
                  value={cakeMessage}
                  onChange={(e) => setCakeMessage(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              
              {!isWeddingCake(cakeId) && (
                <div style={{ marginTop: 16 }}>
                  <h5 style={{ margin: '16px 0 8px 0' }}>Quantity</h5>
                  <InputNumber
                    min={1}
                    max={10}
                    defaultValue={1}
                    value={quantity}
                    onChange={(value) => setQuantity(value)}
                    style={{ width: 'min(120px, 100%)' }}
                  />
                </div>
              )}
              
              <div style={{ marginTop: 16 }}>
                <h5 style={{ margin: '16px 0 8px 0' }}>Contact Information</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Your name *</label>
                    <Input 
                      placeholder="Full name" 
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 500 }}>Phone number *</label>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px',
                      backgroundColor: '#fff',
                      overflow: 'hidden',
                      width: 'min(200px, 100%)'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '4px 8px',
                        backgroundColor: '#f5f5f5',
                        borderRight: '1px solid #d9d9d9',
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        🇨🇦 +1
                      </div>
                      <Input
                          placeholder="(123) 456-7890"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          style={{
                            border: 'none',
                            boxShadow: 'none',
                            flex: 1
                          }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Requirements</label>
                    <Input 
                      placeholder="Such as allergy restrictions, topper, design, etc.." 
                      value={customerRequirement}
                      onChange={(e) => setCustomerRequirement(e.target.value)}
                      style={{ width: '100%' }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Pick up date *</label>
                    <DatePicker
                      value={customerPreferredDate}
                      onChange={(date) => setCustomerPreferredDate(date)}
                      disabledDate={(date) => date && (date.isBefore(minDate) || date.isAfter(maxDate))}
                      style={{ width: '100%' }}
                      placeholder="Select date"
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Delivery Option</label>
                    <Radio.Group 
                      onChange={(e) => setDeliveryValue(e.target.value)} 
                      value={deliveryValue}
                      style={{ fontSize: 14 }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Radio value={1}>Pickup</Radio>
                        <Radio value={2}>
                          {isNewWeddingCake ? (
                            <>
                              Delivery (free in{' '}
                              <a
                                href="https://maps-vancouver.com/vancouver-lower-mainland-map"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Vancouver mainland
                              </a>
                              )
                            </>
                          ) : (
                            'Delivery (additional fee applies)'
                          )}
                        </Radio>
                      </div>
                    </Radio.Group>
                  </div>
                  
                  {deliveryValue === 2 && (
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Delivery address *</label>
                      <Input 
                        placeholder="Insert delivery address" 
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                  
                  {deliveryValue === 1 && (
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Pickup Address</label>
                      <div style={{ 
                        padding: '8px 12px', 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: '6px',
                        border: '1px solid #d9d9d9',
                        fontSize: '14px',
                        color: '#333'
                      }}>
                        <a 
                          href="https://maps.app.goo.gl/XTkurEjEJHPiG9op7" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            color: '#1890ff', 
                            textDecoration: 'none',
                            fontWeight: '500',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          📍 Willoughby Town Centre
                        </a>
                        , Langley
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <OrderButton
                cakeName={displayName}
                cakeId={cakeId}
                selectedSize={selectedSizeLabel}
                selectedFlavour={selectedFlavourLabel}
                cakeMessage={cakeMessage}
                phoneNumber={phoneNumber}
                quantity={quantity}
                customerName={customerName}
                customerRequirement={customerRequirement}
                customerPreferredDate={customerPreferredDate}
                deliveryValue={deliveryValue}
                deliveryAddress={deliveryAddress}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cake;

